import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  Mail, Send, AlertTriangle, HelpCircle, Briefcase, 
  MessageSquare, Bug, Shield, FileQuestion, CheckCircle,
  Loader2, Building, Heart
} from 'lucide-react';
import { Button } from '../components/ui/button';
import NavigationMenu from '../components/NavigationMenu';
import DynamicFooter from '../components/DynamicFooter';
import SEOHead from '../components/SEOHead';
import ReCaptcha, { useCaptcha } from '../components/ReCaptcha';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const contactReasons = [
  {
    id: 'abuse',
    icon: AlertTriangle,
    label: 'Signaler un abus',
    description: 'Contenu illégal, spam, violation des CGU',
    color: 'red',
    fields: ['url', 'description']
  },
  {
    id: 'bug',
    icon: Bug,
    label: 'Signaler un bug',
    description: 'Dysfonctionnement, erreur technique',
    color: 'orange',
    fields: ['browser', 'description']
  },
  {
    id: 'gdpr',
    icon: Shield,
    label: 'Demande RGPD',
    description: 'Accès, rectification, suppression de données',
    color: 'green',
    fields: ['gdpr_type', 'description']
  },
  {
    id: 'business',
    icon: Briefcase,
    label: 'Partenariat / Entreprise',
    description: 'Offre entreprise, intégration, API',
    color: 'purple',
    fields: ['company', 'description']
  },
  {
    id: 'feedback',
    icon: Heart,
    label: 'Suggestion / Feedback',
    description: 'Idée d\'amélioration, retour d\'expérience',
    color: 'pink',
    fields: ['description']
  },
  {
    id: 'other',
    icon: MessageSquare,
    label: 'Autre demande',
    description: 'Question générale, information',
    color: 'cyan',
    fields: ['subject', 'description']
  }
];

const gdprTypes = [
  { value: 'access', label: 'Droit d\'accès - Obtenir une copie de mes données' },
  { value: 'rectify', label: 'Droit de rectification - Corriger mes données' },
  { value: 'delete', label: 'Droit à l\'effacement - Supprimer mes données' },
  { value: 'portability', label: 'Droit à la portabilité - Exporter mes données' },
  { value: 'opposition', label: 'Droit d\'opposition - M\'opposer au traitement' }
];

const ContactPage = () => {
  const location = useLocation();
  const [selectedReason, setSelectedReason] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    url: '',
    description: '',
    browser: '',
    gdpr_type: '',
    company: '',
    subject: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Reset form when URL changes (e.g., clicking Contact link from footer)
  useEffect(() => {
    setSelectedReason(null);
    setFormData({
      name: '',
      email: '',
      url: '',
      description: '',
      browser: '',
      gdpr_type: '',
      company: '',
      subject: ''
    });
    setSubmitted(false);
    setError('');
  }, [location.search]); // Trigger on query param change

  const colorClasses = {
    red: 'bg-red-500/20 text-red-400 border-red-500/30 hover:border-red-500/50',
    blue: 'bg-blue-500/20 text-blue-400 border-blue-500/30 hover:border-blue-500/50',
    orange: 'bg-orange-500/20 text-orange-400 border-orange-500/30 hover:border-orange-500/50',
    green: 'bg-green-500/20 text-green-400 border-green-500/30 hover:border-green-500/50',
    purple: 'bg-purple-500/20 text-purple-400 border-purple-500/30 hover:border-purple-500/50',
    pink: 'bg-pink-500/20 text-pink-400 border-pink-500/30 hover:border-pink-500/50',
    cyan: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30 hover:border-cyan-500/50'
  };

  const selectedColorClasses = {
    red: 'bg-red-500/30 border-red-500 ring-2 ring-red-500/30',
    blue: 'bg-blue-500/30 border-blue-500 ring-2 ring-blue-500/30',
    orange: 'bg-orange-500/30 border-orange-500 ring-2 ring-orange-500/30',
    green: 'bg-green-500/30 border-green-500 ring-2 ring-green-500/30',
    purple: 'bg-purple-500/30 border-purple-500 ring-2 ring-purple-500/30',
    pink: 'bg-pink-500/30 border-pink-500 ring-2 ring-pink-500/30',
    cyan: 'bg-cyan-500/30 border-cyan-500 ring-2 ring-cyan-500/30'
  };

  // CAPTCHA
  const { captchaToken, captchaKey, resetCaptcha, onVerify, onExpire, onError, isVerified, captchaConfig } = useCaptcha();
  const captchaRequired = captchaConfig?.enabled && captchaConfig?.protect_contact;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedReason) return;
    
    if (captchaRequired && !isVerified) {
      setError('Veuillez valider le CAPTCHA');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await axios.post(`${API}/api/contact`, {
        reason: selectedReason.id,
        reason_label: selectedReason.label,
        captcha_token: captchaToken,
        ...formData
      });
      setSubmitted(true);
    } catch (err) {
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedReason(null);
    setFormData({
      name: '',
      email: '',
      url: '',
      description: '',
      browser: '',
      gdpr_type: '',
      company: '',
      subject: ''
    });
    setSubmitted(false);
    setError('');
    resetCaptcha();
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
        <NavigationMenu />
        <main className="pt-24 pb-32">
          <div className="max-w-2xl mx-auto px-6 text-center">
            <div className="bg-slate-800/40 border border-green-500/30 rounded-2xl p-10">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-4">Message envoyé !</h1>
              <p className="text-blue-200/70 mb-6">
                Nous avons bien reçu votre message et vous répondrons dans les plus brefs délais.
                {selectedReason?.id === 'abuse' && (
                  <span className="block mt-2 text-sm text-orange-300">
                    Les signalements d'abus sont traités en priorité sous 24h.
                  </span>
                )}
              </p>
              <Button onClick={resetForm} className="bg-gradient-to-r from-blue-500 to-cyan-500">
                Envoyer un autre message
              </Button>
            </div>
          </div>
        </main>
        <DynamicFooter fixed={true} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
      <SEOHead 
        title="Contact" 
        description="Contactez l'équipe SENDBOX : signaler un abus, poser une question commerciale, demander un partenariat ou nous faire part de vos suggestions." 
      />
      <NavigationMenu />
      
      <main className="pt-24 pb-32">
        <div className="max-w-4xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full text-blue-300 text-sm mb-6">
              <Mail className="w-4 h-4" />
              Contactez-nous
            </div>
            <h1 className="text-4xl font-bold text-white mb-4">Contact</h1>
            <p className="text-lg text-blue-200/70 max-w-2xl mx-auto">
              Choisissez le motif de votre demande pour nous aider à vous répondre plus rapidement.
            </p>
          </div>

          {/* Reason Selection */}
          {!selectedReason ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="contact-reasons">
              {contactReasons.map((reason) => {
                const Icon = reason.icon;
                return (
                  <button
                    key={reason.id}
                    onClick={() => setSelectedReason(reason)}
                    className={`text-left p-5 rounded-xl border transition-all ${colorClasses[reason.color]}`}
                    data-testid={`reason-${reason.id}`}
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${colorClasses[reason.color]}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-white font-semibold mb-1">{reason.label}</h3>
                    <p className="text-sm text-blue-300/60">{reason.description}</p>
                  </button>
                );
              })}
            </div>
          ) : (
            /* Contact Form */
            <div className="max-w-2xl mx-auto">
              {/* Selected reason header */}
              <button
                onClick={() => setSelectedReason(null)}
                className={`w-full text-left p-4 rounded-xl border mb-6 transition-all ${selectedColorClasses[selectedReason.color]}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClasses[selectedReason.color]}`}>
                    <selectedReason.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">{selectedReason.label}</h3>
                    <p className="text-sm text-blue-300/60">Cliquez pour changer</p>
                  </div>
                </div>
              </button>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-blue-200/80 mb-2">
                    Votre nom
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800/50 border border-blue-500/20 rounded-xl text-white placeholder-blue-300/40 focus:outline-none focus:border-blue-500/50 transition-colors"
                    placeholder="Jean Dupont"
                    data-testid="contact-name"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-blue-200/80 mb-2">
                    Votre email
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800/50 border border-blue-500/20 rounded-xl text-white placeholder-blue-300/40 focus:outline-none focus:border-blue-500/50 transition-colors"
                    placeholder="jean@exemple.com"
                    data-testid="contact-email"
                  />
                </div>

                {/* URL field for abuse reports */}
                {selectedReason.fields.includes('url') && (
                  <div>
                    <label className="block text-sm font-medium text-blue-200/80 mb-2">
                      URL du contenu à signaler
                    </label>
                    <input
                      type="url"
                      required
                      value={formData.url}
                      onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-800/50 border border-blue-500/20 rounded-xl text-white placeholder-blue-300/40 focus:outline-none focus:border-blue-500/50 transition-colors"
                      placeholder="https://sendbox.com/share/..."
                      data-testid="contact-url"
                    />
                  </div>
                )}

                {/* Browser field for bug reports */}
                {selectedReason.fields.includes('browser') && (
                  <div>
                    <label className="block text-sm font-medium text-blue-200/80 mb-2">
                      Navigateur utilisé
                    </label>
                    <select
                      required
                      value={formData.browser}
                      onChange={(e) => setFormData({ ...formData, browser: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-800/50 border border-blue-500/20 rounded-xl text-white focus:outline-none focus:border-blue-500/50 transition-colors"
                      data-testid="contact-browser"
                    >
                      <option value="">Sélectionnez...</option>
                      <option value="chrome">Google Chrome</option>
                      <option value="firefox">Mozilla Firefox</option>
                      <option value="safari">Safari</option>
                      <option value="edge">Microsoft Edge</option>
                      <option value="opera">Opera</option>
                      <option value="other">Autre</option>
                    </select>
                  </div>
                )}

                {/* GDPR type selection */}
                {selectedReason.fields.includes('gdpr_type') && (
                  <div>
                    <label className="block text-sm font-medium text-blue-200/80 mb-2">
                      Type de demande RGPD
                    </label>
                    <select
                      required
                      value={formData.gdpr_type}
                      onChange={(e) => setFormData({ ...formData, gdpr_type: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-800/50 border border-blue-500/20 rounded-xl text-white focus:outline-none focus:border-blue-500/50 transition-colors"
                      data-testid="contact-gdpr"
                    >
                      <option value="">Sélectionnez...</option>
                      {gdprTypes.map((type) => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Company field for business */}
                {selectedReason.fields.includes('company') && (
                  <div>
                    <label className="block text-sm font-medium text-blue-200/80 mb-2">
                      Nom de l'entreprise
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-800/50 border border-blue-500/20 rounded-xl text-white placeholder-blue-300/40 focus:outline-none focus:border-blue-500/50 transition-colors"
                      placeholder="ACME Inc."
                      data-testid="contact-company"
                    />
                  </div>
                )}

                {/* Subject field for other */}
                {selectedReason.fields.includes('subject') && (
                  <div>
                    <label className="block text-sm font-medium text-blue-200/80 mb-2">
                      Sujet
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-800/50 border border-blue-500/20 rounded-xl text-white placeholder-blue-300/40 focus:outline-none focus:border-blue-500/50 transition-colors"
                      placeholder="Objet de votre demande"
                      data-testid="contact-subject"
                    />
                  </div>
                )}

                {/* Description - always shown */}
                <div>
                  <label className="block text-sm font-medium text-blue-200/80 mb-2">
                    {selectedReason.id === 'abuse' ? 'Description de l\'abus' : 
                     selectedReason.id === 'bug' ? 'Description du bug' :
                     selectedReason.id === 'feedback' ? 'Votre suggestion' :
                     'Votre message'}
                  </label>
                  <textarea
                    required
                    rows={5}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800/50 border border-blue-500/20 rounded-xl text-white placeholder-blue-300/40 focus:outline-none focus:border-blue-500/50 transition-colors resize-none"
                    placeholder={
                      selectedReason.id === 'abuse' ? 'Décrivez le contenu problématique et pourquoi il doit être signalé...' :
                      selectedReason.id === 'bug' ? 'Décrivez le bug rencontré, les étapes pour le reproduire...' :
                      selectedReason.id === 'feedback' ? 'Partagez votre idée ou suggestion...' :
                      'Décrivez votre demande en détail...'
                    }
                    data-testid="contact-description"
                  />
                </div>

                {error && (
                  <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-300 text-sm">
                    {error}
                  </div>
                )}

                {/* CAPTCHA */}
                {captchaRequired && (
                <div className="flex justify-center">
                  <ReCaptcha 
                    key={captchaKey}
                    onVerify={onVerify} 
                    onExpire={onExpire}
                    onError={onError}
                    siteKey={captchaConfig?.site_key}
                  />
                </div>
                )}

                {/* Submit button */}
                <Button
                  type="submit"
                  disabled={isSubmitting || (captchaRequired && !isVerified)}
                  className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 py-6 disabled:opacity-50"
                  data-testid="contact-submit"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Envoyer le message
                    </>
                  )}
                </Button>

                {/* Info notice */}
                {selectedReason.id === 'abuse' && (
                  <p className="text-sm text-orange-300/70 text-center">
                    Les signalements d'abus sont traités en priorité. En cas d'urgence, 
                    contactez directement les autorités compétentes.
                  </p>
                )}
              </form>
            </div>
          )}
        </div>
      </main>

      <DynamicFooter fixed={true} />
    </div>
  );
};

export default ContactPage;
